import React, { useState, useEffect } from 'react';
import {
  Store, Check, X, Save, Eye, EyeOff, Settings, Search,
  Sparkles, Zap, Crown, Star, Palette, LayoutGrid, ShoppingBag,
  Smartphone, Shirt, ShoppingCart, Gem, Sofa, Dumbbell, Crown as CrownIcon,
  Video, Rocket, Globe, Layers
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// All Shop Templates with Metaverse-style additions
const ALL_SHOP_TEMPLATES = [
  // Metaverse/Futuristic Templates (NEW)
  { 
    id: 'metaverse_store', 
    name: 'Metaverse Store', 
    category: 'metaverse',
    description: 'Futuristic neon-glowing e-commerce experience',
    color: '#00FFFF',
    isPremium: true,
    features: ['3D Product Views', 'Holographic UI', 'Neon Animations', 'VR Ready']
  },
  { 
    id: 'cyberpunk_market', 
    name: 'Cyberpunk Market', 
    category: 'metaverse',
    description: 'Dark dystopian aesthetic with glitch effects',
    color: '#FF00FF',
    isPremium: true,
    features: ['Glitch Animations', 'Neon Borders', 'Scan Lines', 'Matrix Rain']
  },
  { 
    id: 'hologram_shop', 
    name: 'Hologram Shop', 
    category: 'metaverse',
    description: 'Holographic product cards with 3D effects',
    color: '#00FF88',
    isPremium: true,
    features: ['Holographic Cards', 'Floating Elements', 'Scan Effects', 'Glow UI']
  },
  { 
    id: 'quantum_commerce', 
    name: 'Quantum Commerce', 
    category: 'metaverse',
    description: 'Quantum-inspired with wave animations',
    color: '#8B5CF6',
    isPremium: true,
    features: ['Wave Animations', 'Particle Effects', 'Gradient Flows', 'Pulse UI']
  },
  
  // Flipkart-Style Premium Templates
  { 
    id: 'flipkart_marketplace', 
    name: 'Multi-Category Marketplace', 
    category: 'flipkart',
    description: 'Full Flipkart/Amazon style with mega menus',
    color: '#2874F0',
    isPremium: true,
    features: ['Mega Menu', 'Flash Sales', 'Emergency Offers', 'Category Grid']
  },
  { 
    id: 'amazon_marketplace', 
    name: 'Premium Marketplace Pro', 
    category: 'flipkart',
    description: 'Amazon-style marketplace with advanced features',
    color: '#FF9900',
    isPremium: true,
    features: ['Deal of Day', 'Recently Viewed', 'Comparison', 'Brand Showcase']
  },
  { 
    id: 'electronics_store', 
    name: 'Electronics & Gadgets', 
    category: 'flipkart',
    description: 'Modern tech store for electronics',
    color: '#00D4FF',
    isPremium: true,
    features: ['Tech Specs', 'Video Reviews', 'Comparison Table', 'Flash Sale']
  },
  { 
    id: 'fashion_store', 
    name: 'Fashion & Clothing', 
    category: 'flipkart',
    description: 'Trendy fashion store with lookbooks',
    color: '#E91E63',
    isPremium: true,
    features: ['Size Guide', 'Color Variants', 'Lookbook', 'Video Showcase']
  },
  { 
    id: 'grocery_store', 
    name: 'Grocery & Supermarket', 
    category: 'flipkart',
    description: 'Fresh groceries and daily essentials',
    color: '#4CAF50',
    isPremium: false,
    features: ['Quick Delivery', 'Bulk Orders', 'Category Sidebar', 'Deals']
  },
  { 
    id: 'jewelry_store', 
    name: 'Jewelry & Gold', 
    category: 'flipkart',
    description: 'Luxury jewelry and gold store',
    color: '#FFD700',
    isPremium: true,
    features: ['360° View', 'Certification', 'Live Gold Rates', 'Premium UI']
  },
  { 
    id: 'furniture_store', 
    name: 'Furniture & Home', 
    category: 'flipkart',
    description: 'Home furniture and decor store',
    color: '#8D6E63',
    isPremium: false,
    features: ['Room Visualizer', 'Assembly Info', 'Dimension Display', 'Gallery']
  },
  { 
    id: 'beauty_store', 
    name: 'Beauty & Cosmetics', 
    category: 'flipkart',
    description: 'Beauty products and cosmetics',
    color: '#E91E63',
    isPremium: false,
    features: ['Skin Type Filter', 'Video Tutorials', 'Reviews', 'Flash Sale']
  },
  { 
    id: 'sports_store', 
    name: 'Sports & Fitness', 
    category: 'flipkart',
    description: 'Sports equipment and fitness gear',
    color: '#FF5722',
    isPremium: false,
    features: ['Size Guide', 'Brand Filter', 'Activity Filter', 'Deals']
  },
  { 
    id: 'retail_store', 
    name: 'General Retail', 
    category: 'flipkart',
    description: 'Multi-purpose retail store template',
    color: '#7C4DFF',
    isPremium: false,
    features: ['Category Grid', 'Flash Sale', 'Deals', 'Contact']
  },
  
  // Classic Templates
  { 
    id: 'ecommerce_starter', 
    name: 'E-Commerce Starter', 
    category: 'classic',
    description: 'Simple starter template for online stores',
    color: '#D4AF37',
    isPremium: false,
    features: ['Products Grid', 'Features', 'Testimonials', 'Contact']
  },
  { 
    id: 'portfolio_minimal', 
    name: 'Portfolio Minimal', 
    category: 'classic',
    description: 'Clean portfolio showcase template',
    color: '#10B981',
    isPremium: false,
    features: ['Gallery', 'About', 'Testimonials', 'Contact']
  },
  { 
    id: 'business_professional', 
    name: 'Business Professional', 
    category: 'classic',
    description: 'Corporate and professional look',
    color: '#3B82F6',
    isPremium: false,
    features: ['Hero', 'Features', 'Team', 'Clients']
  },
  { 
    id: 'landing_page', 
    name: 'Landing Page', 
    category: 'classic',
    description: 'High-converting landing page',
    color: '#F97316',
    isPremium: false,
    features: ['Hero', 'Features', 'Stats', 'CTA']
  },
];

const TEMPLATE_ICONS = {
  metaverse_store: Sparkles,
  cyberpunk_market: Zap,
  hologram_shop: Globe,
  quantum_commerce: Layers,
  flipkart_marketplace: ShoppingBag,
  amazon_marketplace: Crown,
  electronics_store: Smartphone,
  fashion_store: Shirt,
  grocery_store: ShoppingCart,
  jewelry_store: Gem,
  furniture_store: Sofa,
  beauty_store: Sparkles,
  sports_store: Dumbbell,
  retail_store: Store,
  ecommerce_starter: ShoppingBag,
  portfolio_minimal: Globe,
  business_professional: Store,
  landing_page: Rocket,
};

const CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: LayoutGrid },
  { id: 'metaverse', name: 'Metaverse', icon: Sparkles },
  { id: 'flipkart', name: 'Flipkart-Style', icon: ShoppingBag },
  { id: 'classic', name: 'Classic', icon: Store },
];

const ShopTemplateAdmin = () => {
  const [settings, setSettings] = useState({
    enabled_templates: ALL_SHOP_TEMPLATES.map(t => t.id),
    premium_only_templates: ALL_SHOP_TEMPLATES.filter(t => t.isPremium).map(t => t.id),
    member_allowed_templates: ALL_SHOP_TEMPLATES.filter(t => !t.isPremium).map(t => t.id),
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/shops/template-permissions');
      if (response.data?.permissions) {
        setSettings(prev => ({
          ...prev,
          ...response.data.permissions,
          member_allowed_templates: response.data.permissions.member_allowed_templates || 
                                   response.data.permissions.default_member_access || []
        }));
      }
    } catch (error) {
      console.log('Using default shop template settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/shops/template-permissions', settings);
      toast.success('Shop template settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = (templateId) => {
    setSettings(prev => ({
      ...prev,
      enabled_templates: prev.enabled_templates?.includes(templateId)
        ? prev.enabled_templates.filter(id => id !== templateId)
        : [...(prev.enabled_templates || []), templateId]
    }));
  };

  const toggleMemberAccess = (templateId) => {
    setSettings(prev => ({
      ...prev,
      member_allowed_templates: prev.member_allowed_templates?.includes(templateId)
        ? prev.member_allowed_templates.filter(id => id !== templateId)
        : [...(prev.member_allowed_templates || []), templateId]
    }));
  };

  const enableAllInCategory = (category) => {
    const categoryTemplates = category === 'all' 
      ? ALL_SHOP_TEMPLATES.map(t => t.id)
      : ALL_SHOP_TEMPLATES.filter(t => t.category === category).map(t => t.id);
    setSettings(prev => ({
      ...prev,
      enabled_templates: [...new Set([...(prev.enabled_templates || []), ...categoryTemplates])]
    }));
  };

  const disableAllInCategory = (category) => {
    const categoryTemplates = category === 'all'
      ? ALL_SHOP_TEMPLATES.map(t => t.id)
      : ALL_SHOP_TEMPLATES.filter(t => t.category === category).map(t => t.id);
    setSettings(prev => ({
      ...prev,
      enabled_templates: (prev.enabled_templates || []).filter(id => !categoryTemplates.includes(id))
    }));
  };

  const filteredTemplates = ALL_SHOP_TEMPLATES.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="shop-template-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
              Shop Template Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Control Flipkart-style & Metaverse shop templates for members
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-gradient-to-r from-primary to-blue-500 text-primary-foreground rounded-xl font-semibold flex items-center gap-2 disabled:opacity-50 hover:shadow-lg transition-all"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            Save Changes
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Eye className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-500">{settings.enabled_templates?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Enabled</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-500">{settings.member_allowed_templates?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Member Access</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-500">
                  {ALL_SHOP_TEMPLATES.filter(t => t.category === 'metaverse').length}
                </p>
                <p className="text-sm text-muted-foreground">Metaverse</p>
              </div>
            </div>
          </div>
          <div className="glass-card p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-500">
                  {ALL_SHOP_TEMPLATES.filter(t => t.category === 'flipkart').length}
                </p>
                <p className="text-sm text-muted-foreground">Flipkart-Style</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => enableAllInCategory(selectedCategory)}
            className="px-4 py-2 bg-green-500/20 text-green-500 rounded-xl text-sm font-medium hover:bg-green-500/30"
          >
            Enable All {selectedCategory !== 'all' ? CATEGORIES.find(c => c.id === selectedCategory)?.name : ''}
          </button>
          <button
            onClick={() => disableAllInCategory(selectedCategory)}
            className="px-4 py-2 bg-red-500/20 text-red-500 rounded-xl text-sm font-medium hover:bg-red-500/30"
          >
            Disable All {selectedCategory !== 'all' ? CATEGORIES.find(c => c.id === selectedCategory)?.name : ''}
          </button>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredTemplates.map(template => {
            const isEnabled = settings.enabled_templates?.includes(template.id);
            const hasMemberAccess = settings.member_allowed_templates?.includes(template.id);
            const Icon = TEMPLATE_ICONS[template.id] || Store;

            return (
              <div
                key={template.id}
                className={`relative rounded-2xl overflow-hidden border-2 transition-all ${
                  isEnabled 
                    ? 'border-green-500/50 bg-green-500/5' 
                    : 'border-white/10 bg-white/[0.02] opacity-60'
                }`}
              >
                {/* Color Preview */}
                <div 
                  className="h-28 relative"
                  style={{ 
                    background: `linear-gradient(135deg, ${template.color}40, ${template.color}10)`,
                  }}
                >
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: `radial-gradient(circle at 30% 30%, ${template.color}30, transparent 70%)`
                    }}
                  />
                  {/* Icon */}
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {/* Category Badge */}
                  <span className="absolute top-4 right-4 px-2 py-1 bg-black/40 backdrop-blur-sm rounded-lg text-xs font-medium capitalize">
                    {template.category}
                  </span>
                  {/* Premium Badge */}
                  {template.isPremium && (
                    <span className="absolute bottom-2 right-2 px-2 py-1 bg-amber-500/90 rounded-lg text-xs font-bold flex items-center gap-1">
                      <Crown className="w-3 h-3" />
                      Premium
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: template.color }}
                    />
                    <h3 className="font-bold text-sm">{template.name}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{template.description}</p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.features?.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-white/5 rounded text-[10px] text-muted-foreground">
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleEnabled(template.id)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
                        isEnabled
                          ? 'bg-green-500 text-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                    >
                      {isEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                      {isEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => toggleMemberAccess(template.id)}
                      className={`px-3 py-2 rounded-xl transition-all ${
                        hasMemberAccess
                          ? 'bg-blue-500 text-white'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      title={hasMemberAccess ? 'Member Access: ON' : 'Member Access: OFF'}
                    >
                      <Crown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 flex items-center justify-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500" />
            <span>Enabled</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500" />
            <span>Member Access</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500" />
            <span>Premium</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopTemplateAdmin;
